import { formatInTimeZone } from 'date-fns-tz';
import { differenceInDays, startOfYear } from 'date-fns';

class DateService {
  constructor() {
    // Use America/New_York timezone as the base timezone for all calculations
    this.timezone = 'America/New_York';
  }

  /**
   * Calculates the day of year, accounting for timezone and DST
   * @param {Date} date - The date to calculate for
   * @returns {number} - Day of year (1-366)
   */
  getDayOfYear(date) {
    // Convert to Date object if string
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Get the start of the year for the given date
    const yearStart = startOfYear(dateObj);

    // Calculate the difference in days
    return differenceInDays(dateObj, yearStart) + 1;
  }

  /**
   * Gets the source day of year for content lookup
   * Maps future years (2026+) to 2025 content based on day-of-month
   * @param {Date} date - The date to get source day for
   * @returns {number} - Day of year to use for content lookup (1-366)
   */
  getSourceDayOfYear(date) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const year = dateObj.getFullYear();

    // For 2025, use the actual day of year
    if (year === 2025) {
      return this.getDayOfYear(dateObj);
    }

    // For 2026 and beyond, map to 2025 based on month/day
    // Create a date with same month/day but in 2025
    const sourceDate = new Date(2025, dateObj.getMonth(), dateObj.getDate());
    return this.getDayOfYear(sourceDate);
  }

  /**
   * Formats a date to match the CSV format (M/D/YYYY)
   * @param {Date} date - The date to format
   * @returns {string} - Formatted date string
   */
  formatDateForCSV(date) {
    // Convert to Date object if string
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Format as M/D/YYYY
    return formatInTimeZone(dateObj, this.timezone, 'M/d/yyyy');
  }

  /**
   * Gets the display date for a given day of year
   * @param {number} dayOfYear - The day of year (1-366)
   * @param {number} year - The year (defaults to current year)
   * @returns {string} - Formatted date string (M/D/YYYY)
   */
  getDisplayDateForDayOfYear(dayOfYear, year = new Date().getFullYear()) {
    const date = new Date(year, 0, dayOfYear);
    return this.formatDateForCSV(date);
  }

  /**
   * Validates if a given day number is valid for the year
   * @param {number} day - The day number to validate
   * @param {number} year - The year to validate against
   * @returns {boolean} - Whether the day is valid
   */
  isValidDayOfYear(day, year = new Date().getFullYear()) {
    const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const maxDays = isLeapYear ? 366 : 365;
    return day >= 1 && day <= maxDays;
  }
}

export const dateService = new DateService();
export default dateService; 